import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Gift, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import { BRAND } from '../data/brand';

const GIFT_OPTIONS = [
  { id: 'gift-75', amount: 75, label: '$75' },
  { id: 'gift-150', amount: 150, label: '$150' },
  { id: 'gift-250', amount: 250, label: '$250' },
  { id: 'gift-500', amount: 500, label: '$500' },
];

const GiftPage = () => {
  const navigate = useNavigate();
  const [selectedAmount, setSelectedAmount] = useState(150);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [sent, setSent] = useState(false);

  const handleSend = (e) => {
    e.preventDefault();
    if (recipientEmail && recipientName) {
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Gifting
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
            data-testid="gift-heading"
          >
            Gift a Secret
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500 max-w-lg mx-auto leading-relaxed"
          >
            The finest gift is access. Give someone the key to the collection.
          </motion.p>
        </div>
      </section>

      {sent ? (
        /* Confirmation */
        <section className="py-24 px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="max-w-md mx-auto text-center"
            data-testid="gift-confirmation"
          >
            <Gift className="w-8 h-8 text-zinc-400 mx-auto mb-6" />
            <h2 className="font-serif text-3xl text-zinc-50 mb-4">Secret Sent</h2>
            <p className="font-sans text-sm text-zinc-500 mb-2">
              A ${selectedAmount} gift card has been sent to {recipientName}.
            </p>
            <p className="font-sans text-xs text-zinc-600 mb-8">They'll receive an email with instructions to access the collection.</p>
            <button
              onClick={() => navigate('/collection')}
              className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 font-sans text-xs tracking-[0.2em] uppercase hover:bg-zinc-200 transition-all duration-300"
            >
              Continue Browsing
              <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </section>
      ) : (
        /* Gift Form */
        <section className="py-8 px-6 md:px-12 lg:px-24">
          <div className="max-w-lg mx-auto">
            {/* Amount Selection */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="mb-12"
            >
              <p className="font-sans text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4 text-center">Select Amount</p>
              <div className="grid grid-cols-4 gap-3">
                {GIFT_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setSelectedAmount(option.amount)}
                    data-testid={`gift-amount-${option.amount}`}
                    className={`py-4 font-sans text-sm transition-all duration-300 ${
                      selectedAmount === option.amount
                        ? 'bg-white text-black'
                        : 'border border-white/10 text-zinc-400 hover:border-white/25'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Recipient Form */}
            <motion.form
              onSubmit={handleSend}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="space-y-6"
            >
              <div>
                <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600 block mb-2">Recipient Name</label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  required
                  data-testid="gift-recipient-name"
                  className="w-full bg-transparent border-b border-white/10 py-3 font-sans text-sm text-zinc-200 placeholder:text-zinc-700 focus:outline-none focus:border-white/30 transition-colors"
                  placeholder="Their name"
                />
              </div>
              <div>
                <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600 block mb-2">Recipient Email</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  required
                  data-testid="gift-recipient-email"
                  className="w-full bg-transparent border-b border-white/10 py-3 font-sans text-sm text-zinc-200 placeholder:text-zinc-700 focus:outline-none focus:border-white/30 transition-colors"
                  placeholder="their@email.com"
                />
              </div>
              <div>
                <label className="font-sans text-[10px] tracking-[0.2em] uppercase text-zinc-600 block mb-2">Personal Message (Optional)</label>
                <textarea
                  value={personalMessage}
                  onChange={(e) => setPersonalMessage(e.target.value)}
                  data-testid="gift-message"
                  rows={3}
                  className="w-full bg-transparent border-b border-white/10 py-3 font-sans text-sm text-zinc-200 placeholder:text-zinc-700 focus:outline-none focus:border-white/30 transition-colors resize-none"
                  placeholder="Add a personal touch..."
                />
              </div>

              {/* Summary */}
              <div className="border border-white/5 p-6 mt-8">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-sans text-xs text-zinc-500">Gift Card Value</p>
                  <p className="font-serif text-xl text-zinc-50">${selectedAmount}</p>
                </div>
                <button
                  type="submit"
                  data-testid="send-gift"
                  className="w-full bg-white text-black py-4 font-sans text-xs tracking-[0.2em] uppercase hover:bg-zinc-200 transition-all duration-300"
                >
                  Send Gift
                </button>
              </div>
            </motion.form>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5 mt-16">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default GiftPage;
